import LabelImportantIcon from "@mui/icons-material/LabelImportantRounded";
import { Box, Divider, IconButton, Paper, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import HtmlPreview from "@entrilia/shared/components/html/HtmlPreview";
import { convertISODateTimeWithSeconds } from "@entrilia/shared/utilities/dateUtils";
import useInboxStateContext from "../../../contexts/useInboxStateContext";
import type { PreviewDocument } from "../../../hooks/pdfPreview";
import { getFundsFromMessage, getRecipientsFromMessage } from "../../../store/state/messages/messageHelper";
import { currentMessageSelector, markAsImportantUpdatingSelector } from "../../../store/state/messages/selectors";
import { markAsImportant } from "../../../store/state/messages/thunks";
import { impersonationSelector } from "../../../store/state/user/selectors";
import type { AppDispatch } from "../../../store/store";
import InvestorPortalPreviewFileDialog from "../../common/InvestorPortalPreviewFileDialog";
import MessageCategoryTag from "../MessageCategoryTag";
import AttachmentsContainer from "./AttachmentsContainer";

interface Props {
  downloadAttachment: (messageId: string, attachmentId: string) => void;
}

// Message bodies are not authored in HtmlEditor, so keep the default paragraph spacing HtmlPreview resets.
const messageBodyStyles = "p { margin: 1em 0; } p:first-child { margin-top: 0; } p:last-child { margin-bottom: 0; }";

const MessageContent = ({ downloadAttachment }: Props) => {
  const [previewFileData, setPreviewFileData] = useState<PreviewDocument | undefined>(undefined);

  const dispatch: AppDispatch = useDispatch();
  const message = useSelector(currentMessageSelector);
  const applyingImportance = useSelector(markAsImportantUpdatingSelector);
  const isImpersonation = useSelector(impersonationSelector);

  const { ui } = useInboxStateContext();

  const changeImportance = useCallback(() => {
    if (message) {
      dispatch(markAsImportant([message.id], !message.isImportant));
    }
  }, [message, dispatch]);

  const handlePreview = useCallback((downloadData: PreviewDocument) => {
    setPreviewFileData(downloadData);
    window.scrollTo(0, 0);
  }, []);

  if (message === undefined) {
    return null;
  }

  const recipients = getRecipientsFromMessage(message);
  const toText = recipients.length > 1 ? "mutiple recipients" : recipients[0];

  const funds = getFundsFromMessage(message);

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
      <Stack spacing={1.5} sx={{ width: "100%", p: 2.5 }}>
        <Typography variant="subtitle2">{funds.join(", ")}</Typography>
        <Stack spacing={1} sx={{ width: "100%", alignItems: "center" }}>
          <MessageCategoryTag categoryId={message.categoryId} />
          <Typography variant="caption" color="secondary">
            {convertISODateTimeWithSeconds(message.sendDate)}
          </Typography>
        </Stack>
        <Typography variant="subtitle2">To: {toText}</Typography>
      </Stack>
      <Divider />
      <Stack spacing={2} sx={{ width: "100%", px: 2.5, py: 2 }}>
        <Box sx={{ width: "100%", display: "flex", alignItems: "center" }}>
          <Typography variant="h5">{message.content.subject}</Typography>
          <IconButton
            disabled={applyingImportance}
            onClick={() => {
              if (!isImpersonation) {
                changeImportance();
              }
            }}
          >
            <LabelImportantIcon sx={(t) => ({ color: message?.isImportant ? t.palette.warning.light : "inherit" })} />
          </IconButton>
        </Box>
        {message.content.htmlBody && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <HtmlPreview htmlContent={message.content.htmlBody} extraStyles={messageBodyStyles} />
          </Paper>
        )}
        <AttachmentsContainer
          message={message}
          loading={ui.messageDetailLoading}
          onPreview={handlePreview}
          onDownloadAttachment={downloadAttachment}
        />
      </Stack>
      {previewFileData && message && !ui.messageDetailLoading && (
        <InvestorPortalPreviewFileDialog
          url={previewFileData.fileInfo.downloadUrl}
          fileName={previewFileData.fileInfo.fileName}
          documentViewSessionId={previewFileData.fileInfo.documentViewSessionId}
          refreshUrl={previewFileData.refresh}
          preventDownload={previewFileData.preventDownload}
          onClose={() => setPreviewFileData(undefined)}
        />
      )}
    </Box>
  );
};

export default MessageContent;
